
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { DropLi } from "../../hooks";
import { formatCurrency } from "../../hooks/Currency";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Purchase Details Content
//------------------------------------------------
const PurchaseDetailsContent = ({
  viewData,
  store,
  paymentTypes,
  t,
  printMode = false,
}) => {
  const items = Array.isArray(viewData?.purchItems)
    ? viewData.purchItems
    : [];

  const supplierName =
    typeof viewData?.supplier === "string"
      ? viewData.supplier
      : viewData?.supplier?.company || "";

  const purchaseUser = viewData?.user || {};

  const purchaseUserName = [
    purchaseUser.firstName,
    purchaseUser.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  //------------------------------------------------
  // Calculations
  //------------------------------------------------
  const totalQuantity = items.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0
  );

  const subtotal = items.reduce(
    (sum, item) =>
      sum +
      Number(item.qty || 0) *
        Number(item.purchPrice || 0),
    0
  );

  const discountPercent =
    Number(viewData?.discount) || 0;

  const discountAmount =
    (discountPercent / 100) * subtotal;

  const total = subtotal - discountAmount;

  const money = (value) =>
    formatCurrency(Number(value) || 0, store);

  //------------------------------------------------
  // Payment Type
  //------------------------------------------------
  const paymentTypeName =
    paymentTypes.find(
      (item) =>
        String(item.id) ===
        String(viewData?.paymentType)
    )?.name || "";

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Box>
      {/* Report Title */}
      <Box sx={{ textAlign: "center", mt: 2, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
        >
          {t("purchaseDetails")}
        </Typography>

        <Box
          sx={{
            width: "80%",
            borderBottom: "1px solid #ccc",
            mx: "auto",
            mt: 2,
          }}
        />
      </Box>

      {/* Purchased Products */}
      <TableContainer sx={{ mt: 2 }}>
        <Table
          size="small"
          aria-label="purchase items"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("itemName")}
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                {t("barcode")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("quantity")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("price")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("total")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item, index) => {
              const quantity =
                Number(item.qty) || 0;

              const price =
                Number(item.purchPrice) || 0;

              return (
                <TableRow key={item.id ?? index}>
                  <TableCell>
                    {item.product?.name || ""}
                  </TableCell>

                  <TableCell>
                    {item.product?.barcode || ""}
                  </TableCell>

                  <TableCell align="right">
                    {quantity}
                  </TableCell>

                  <TableCell align="right">
                    {money(price)}
                  </TableCell>

                  <TableCell align="right">
                    {money(quantity * price)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      {/* Purchase Summary */}
      <Grid container spacing={2}>
        {/* Left Column */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("referenceNo")}:
                  </TableCell>

                  <TableCell align="right">
                    {viewData?.id ?? ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("supplier")}:
                  </TableCell>

                  <TableCell align="right">
                    {supplierName}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("purchaseUser")}:
                  </TableCell>

                  <TableCell align="right">
                    {purchaseUserName}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("paymentType")}:
                  </TableCell>

                  <TableCell align="right">
                    {paymentTypeName}
                  </TableCell>
                </TableRow>

                {viewData?.paymentDue != null && (
                  <TableRow>
                    <TableCell>
                      {t("paymentDue")}:
                    </TableCell>

                    <TableCell align="right">
                      {money(viewData.paymentDue)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Receipt Link - Screen Only */}
                {!printMode && viewData?.receipt && (
                  <TableRow>
                    <TableCell>
                      {t("purchaseReceipt")}:
                    </TableCell>

                    <TableCell align="right">
                      <a
                        href={viewData.receipt}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("viewReceipt")}
                      </a>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("purchaseDate")}:
                  </TableCell>

                  <TableCell align="right">
                    {viewData?.purchDate || ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalItem")}:
                  </TableCell>

                  <TableCell align="right">
                    {totalQuantity}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("subtotal")}:
                  </TableCell>

                  <TableCell align="right">
                    {money(subtotal)}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("discount")}:
                  </TableCell>

                  <TableCell align="right">
                    {discountPercent}% - (
                    {money(discountAmount)})
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("total")}:
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold" }}
                  >
                    {money(total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

//------------------------------------------------
// View Purchase Details
//------------------------------------------------
const ViewPurch = (props) => {
  const { t } = useTranslation();

  const { setShowForm, viewData } = props;

  const _store = useSelector(
    (state) => state.pos.defStore
  );

  const { PaymentType } = DropLi();

  const reportRef = useRef(null);

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Grid container className="p-20">
      {/* Print Preview Button */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("purchaseDetails")}
        />
      </Grid>

      {/* Screen Purchase Details */}
      <Grid item xs={12}>
        <PurchaseDetailsContent
          viewData={viewData}
          store={_store}
          paymentTypes={PaymentType || []}
          t={t}
        />
      </Grid>

      {/* Print-only Purchase Details */}
      <Grid item xs={12}>
        <Box
          ref={reportRef}
          className="purchase-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <PurchaseDetailsContent
            viewData={viewData}
            store={_store}
            paymentTypes={PaymentType || []}
            t={t}
            printMode={true}
          />
        </Box>
      </Grid>

      {/* Close Button */}
      <Grid
        item
        xs={12}
        className="tx-center mt-40"
      >
        <Button
          onClick={() => setShowForm(false)}
          className="btn-error"
          size="large"
          sx={{ minWidth: "150px" }}
        >
          {t("close")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ViewPurch;
